import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Checkbox, CircularProgress, Tab, Tabs, Tooltip} from "@mui/material";
import {Add, DeleteOutlined, GroupsOutlined} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {ListPageShell, PageActions, PageDialogProvider, usePageDialogs,} from "../../../../../components/page-shell";
import PartnerDataGrid from "../../../../../containers/data-grid";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import useSearch from "../../../../../hooks/use-search";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import CompetitorPriceReportsSearchSection from "./search-section";
import {buildCompetitorPriceReportsListRequest} from "./build-list-request";
import Api from "../../../../../../core/services/api/api";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import {CompetitorPriceReportsDataGridColumnNames} from "../../../../../../core/constants/table/data-grid-column-keys";
import DateUtils from "../../../../../../core/services/date-utils";
import {setReduxDialogState} from "../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../core/models/static/routes/redux-store-routes";
import IconButton from "../../../../../components/base/button/icon_button";
import ReportCompetitorPriceDialog from "../../../../../components/dialogs/report-competitor-price";
import ManageCompetitorsDialog from "../../../../../components/dialogs/manage-competitors";
import {ReactComponent as CheckboxIcon} from "../../../../../../assets/images/check-box/checkbox.svg";
import {ReactComponent as ActiveCheckboxIcon} from "../../../../../../assets/images/check-box/checkbox-active.svg";
import ProductInformationPopover from "../../../../../components/app-specific/draggable-popover/product-information-popover";

const tabs = {all: 'all', mine: 'mine'};
const exportingChunkSize = 100;
const col = CompetitorPriceReportsDataGridColumnNames;
const allGridSave = {name: 'CompetitorPriceReportsAllDataGrid', version: 2};
const mineGridSave = {name: 'CompetitorPriceReportsMineDataGrid', version: 2};
const dialogRegistry = {
    report: ReportCompetitorPriceDialog,
    manage: ManageCompetitorsDialog,
};

/**
 * Inner reports view hosted under PageDialogProvider.
 */
const CompetitorPriceReportViewInner = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.competitor-price-reports'});
    const dispatch = useDispatch();
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const {openDialog} = usePageDialogs();
    const {configureNotebookTags} = useConfigureNotebook('competitor-price-report');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const [data, setData] = useState([]);
    const canViewAll = operatorAccessLevels.packages.lookup.viewAllCompetitorPriceReports;
    const canViewMine = operatorAccessLevels.packages.lookup.viewMyCompetitorPriceReports;
    const defaultTab = canViewAll ? tabs.all : tabs.mine;
    const [updatedTab, setUpdatedTab] = useState(defaultTab);
    const [togglingIds, setTogglingIds] = useState([]);
    const canManageReports = operatorAccessLevels.packages.lookup.manageCompetitorPriceReports;
    const canExportReports = operatorAccessLevels.packages.lookup.exportCompetitorPriceReports;
    const canReport = operatorAccessLevels.packages.lookup.reportCompetitorPrice;
    const canViewCompetitors = operatorAccessLevels.packages.lookup.viewCompetitors
        || operatorAccessLevels.packages.lookup.manageCompetitors;

    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.configureNotebookTags);
        configureBehavioralTool((tags) => tags.CompetitorPriceReportView);
    }, [configureBehavioralTool, configureNotebookTags]);

    const columns = useMemo(() => [
        ...(canManageReports ? [{
            title: t('columns.actions'),
            name: col.actions,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.center,
            sortable: false,
            pinned: true,
            pinnedToggleable: false,
            visibilityToggleable: false,
            width: 56,
        }] : []),
        {
            title: t('columns.checked'),
            name: col.checked,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.center,
            width: 90,
        },
        {title: t('columns.operator'), name: col.operator, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left, width: 150},
        {title: t('columns.partNumber'), name: col.partNumber, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left, width: 150},
        {title: t('columns.price'), name: col.price, type: DataGridColumnTypes.money, alignment: DataGridColumnAlignments.right, width: 150},
        {title: t('columns.competitorPrice'), name: col.competitorPrice, type: DataGridColumnTypes.money, alignment: DataGridColumnAlignments.right, width: 150},
        {title: t('columns.competitor'), name: col.competitor, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left, width: 150},
        {title: t('columns.submitDateTime'), name: col.submitDateTime, type: DataGridColumnTypes.dateTime, alignment: DataGridColumnAlignments.left, width: 180},
        {title: t('columns.customer'), name: col.customer, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {title: t('columns.priceLevel'), name: col.priceLevel, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {title: t('columns.checkedDateTime'), name: col.checkedDateTime, type: DataGridColumnTypes.dateTime, alignment: DataGridColumnAlignments.left, width: 180},
    ], [canManageReports, t]);

    const [allColumns, allSortBy, allPageSize, allDensity] = useDataGridSavedState({
        columns, name: allGridSave.name, version: allGridSave.version,
    });
    const [mineColumns, mineSortBy, minePageSize, mineDensity] = useDataGridSavedState({
        columns, name: mineGridSave.name, version: mineGridSave.version,
    });

    const [savedColumns, savedSortBy, savedPageSize, savedDensity, savedGrid] = updatedTab === tabs.mine
        ? [mineColumns, mineSortBy, minePageSize, mineDensity, mineGridSave]
        : [allColumns, allSortBy, allPageSize, allDensity, allGridSave];

    const initialPaginationInfo = useMemo(() => savedPageSize
        ? {...DefaultPaginationInfo, pageSize: savedPageSize}
        : DefaultPaginationInfo, [savedPageSize]);

    /**
     * Fetches a page of reports. When forExport, returns the response without updating the grid.
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = buildCompetitorPriceReportsListRequest(args);
        const tab = args.tab ?? tabs.all;
        const response = tab === tabs.mine
            ? await Api.listMyCompetitorPriceReports(forApi, args.cancelTokenSource?.token)
            : await Api.listCompetitorPriceReports(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response.data?.items ?? []);
            args.setPaginationInfo?.((prev) => ({
                ...prev,
                length: response.data?.totalCount ?? 0,
            }));
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted]);

    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialSearchSettings = useMemo(() => ({
        tab: updatedTab,
        paginationInfo: initialPaginationInfo,
        orderBy: savedSortBy,
        filters: {},
    }), [initialPaginationInfo, savedSortBy, updatedTab]);

    const searchData = useSearch(search, initialSearchSettings);

    /**
     * Coerces the active tab when the user lacks access to the current one.
     */
    useEffect(() => {
        const currentTab = searchData.tab ?? defaultTab;
        const tabAllowed = (currentTab === tabs.all && canViewAll)
            || (currentTab === tabs.mine && canViewMine);
        if (!tabAllowed) {
            searchData.setTab(defaultTab);
            return;
        }
        setUpdatedTab(currentTab);
    }, [canViewAll, canViewMine, defaultTab, searchData.tab, searchData.setTab]);

    /**
     * Switches All / Mine and restores that tab's saved page size and sort.
     */
    const onTabChanged = useCallback((_, newTab) => {
        if ((newTab === tabs.all && !canViewAll) || (newTab === tabs.mine && !canViewMine))
            return;
        setData([]);
        searchData.setTab(newTab);
        const pageSize = newTab === tabs.mine ? minePageSize : allPageSize;
        const orderBy = newTab === tabs.mine ? mineSortBy : allSortBy;
        searchData.setPaginationInfo((prev) => ({
            ...prev,
            pageSize: pageSize ?? initialPaginationInfo.pageSize,
            currentPage: 1,
        }));
        searchData.setOrderBy(orderBy);
    }, [allPageSize, allSortBy, canViewAll, canViewMine, initialPaginationInfo.pageSize, minePageSize, mineSortBy, searchData]);

    /**
     * Checks or unchecks a report, then patches the row locally on success.
     */
    const toggleChecked = useCallback(async (id, hasChecked) => {
        setTogglingIds((prev) => prev.includes(id) ? prev : [...prev, id]);
        const response = await Api.checkCompetitorPriceReport({id, checked: hasChecked});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData((prev) => prev.map((entry) => (
                entry.id === id
                    ? {
                        ...entry,
                        hasChecked,
                        checkedDateTime: response.data?.checkedDateTime ?? (hasChecked ? entry.checkedDateTime : null),
                    }
                    : entry
            )));
        }
        setTogglingIds((prev) => prev.filter((entryId) => entryId !== id));
    }, [isMounted]);

    /**
     * Confirms then deletes a price report.
     */
    const removeReport = useCallback((row) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            removedEntryTitle: t('removeReportEntry', {part: row.partNumber, competitor: row.competitorName}),
            onClose: async (confirmed) => {
                if (!confirmed)
                    return;
                const response = await Api.removeCompetitorPriceReport(row.id);
                if (!isMounted())
                    return false;
                if (response?.resultFlag)
                    void searchData.performSearch();
                return response?.resultFlag ?? false;
            },
        }));
    }, [dispatch, isMounted, searchData, t]);

    /**
     * Maps report rows to CSV columns.
     */
    const formatExportingData = useCallback((list) => ({
        title: t('exportTitle'),
        body: list?.map((item) => ({
            [t('columns.operator')]: item.operatorFullName ?? '',
            [t('columns.partNumber')]: item.partNumber ?? '',
            [t('columns.price')]: item.price ?? '',
            [t('columns.competitorPrice')]: item.competitorPrice ?? '',
            [t('columns.competitor')]: item.competitorName ?? '',
            [t('columns.submitDateTime')]: item.submitDateTime
                ? DateUtils.serverToLocal(item.submitDateTime).format(DateUtils.dateTimeFormat)
                : '',
            [t('columns.customer')]: item.customerName ?? '',
            [t('columns.priceLevel')]: item.priceLevelTitle ?? '',
            [t('columns.checked')]: item.hasChecked ? t('yes') : t('no'),
            [t('columns.checkedDateTime')]: item.checkedDateTime
                ? DateUtils.serverToLocal(item.checkedDateTime).format(DateUtils.dateTimeFormat)
                : '',
        })) ?? [],
    }), [t]);

    /**
     * Walks paginated report APIs in memory until the full filtered set is collected.
     */
    const exportToCsv = useCallback(async () => {
        const paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        const entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo}, true);
            if (!res?.resultFlag)
                return;
            const items = res.data?.items ?? [];
            if (!items.length)
                break;
            entries.push(...items);
            if (!isMounted())
                return;
            paginationInfo.currentPage++;
        }
        return formatExportingData(entries);
    }, [fetchEntries, formatExportingData, isMounted, searchData]);

    const pageActionItems = useMemo(() => [
        {
            id: 'new-report',
            label: t('actions.newReport'),
            icon: <Add/>,
            hidden: !canReport,
            onClick: () => openDialog('report', {onSuccess: () => searchData.performSearch()}),
        },
        {
            id: 'manage-competitors',
            label: t('actions.manageCompetitors'),
            icon: <GroupsOutlined/>,
            hidden: !canViewCompetitors,
            onClick: () => openDialog('manage'),
        },
    ], [canReport, canViewCompetitors, openDialog, searchData, t]);

    const rows = useMemo(() => data.map((row) => ({
        key: row.id,
        data: row,
        cells: {
            [col.operator]: row.operatorFullName,
            [col.partNumber]: (
                <ProductInformationPopover partNo={row.partNumber}>
                    {row.partNumber ?? ""}
                </ProductInformationPopover>
            ),
            [col.price]: row.price,
            [col.competitorPrice]: row.competitorPrice,
            [col.competitor]: row.competitorName,
            [col.submitDateTime]: row.submitDateTime,
            [col.customer]: row.customerName,
            [col.priceLevel]: row.priceLevelTitle,
            [col.checked]: togglingIds.includes(row.id)
                ? (
                    <Box sx={{p: 1, display: 'flex', justifyContent: 'center'}}>
                        <CircularProgress size={16}/>
                    </Box>
                )
                : (
                    <Checkbox
                        disableRipple
                        icon={<CheckboxIcon/>}
                        checkedIcon={<ActiveCheckboxIcon/>}
                        sx={{py: 0.75}}
                        checked={!!row.hasChecked}
                        disabled={!canManageReports}
                        onChange={(_, checked) => toggleChecked(row.id, checked)}
                        inputProps={{'aria-label': row.hasChecked ? t('actions.uncheck') : t('actions.check')}}
                    />
                ),
            [col.checkedDateTime]: row.checkedDateTime,
            [col.actions]: canManageReports
                ? (
                    <Tooltip title={t('actions.remove')}>
                        <IconButton color={'paper.error'} size={'medium'} onPressed={() => removeReport(row)}>
                            <DeleteOutlined className={'icon medium'}/>
                        </IconButton>
                    </Tooltip>
                )
                : null,
        },
    })), [canManageReports, data, removeReport, t, toggleChecked, togglingIds]);

    return (
        <ListPageShell
            title={t('title')}
            actions={<PageActions items={pageActionItems}/>}
            tabs={(
                <Tabs value={searchData.tab ?? defaultTab} onChange={onTabChanged} variant="scrollable" scrollButtons="auto">
                    {canViewAll && <Tab value={tabs.all} label={t('tabs.all')}/>}
                    {canViewMine && <Tab value={tabs.mine} label={t('tabs.mine')}/>}
                </Tabs>
            )}
            filters={(
                <CompetitorPriceReportsSearchSection
                    searchData={searchData}
                    onManageCompetitors={canViewCompetitors ? () => openDialog('manage') : undefined}
                />
            )}
        >
            <PartnerDataGrid
                {...savedGrid}
                initialLayoutState={{columns}}
                columns={savedColumns}
                rows={rows}
                hideFooterActions
                state={{
                    sortBy: searchData.orderBy,
                    pagination: searchData.paginationInfo,
                    loading: {state: searchData.loading},
                    density: savedDensity,
                }}
                getExportInfo={canExportReports ? exportToCsv : undefined}
                onSortByChanged={(_, current) => searchData.setOrderBy?.(current)}
                onCurrentPageChanged={(_, current) =>
                    searchData.setPaginationInfo((prev) => ({...prev, ...current}))}
                onPageSizeChanged={(_, current) =>
                    searchData.setPaginationInfo((prev) => ({...prev, ...current}))}
            />
        </ListPageShell>
    );
};

const CompetitorPriceReportView = () => (
    <PageDialogProvider registry={dialogRegistry}>
        <CompetitorPriceReportViewInner/>
    </PageDialogProvider>
);

export default CompetitorPriceReportView;
